import React from 'react';
import { cn } from '../../utils';
import Modal, { type ModalSize } from './Modal';

export type CommonModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';

export interface CommonModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  size?: CommonModalSize;
  closeOnOverlayClick?: boolean;
  closeDisabled?: boolean;
  hideCloseButton?: boolean;
  className?: string;
  bodyClassName?: string;
  footerClassName?: string;
  align?: 'center' | 'start';
  overlayClassName?: string;
  zClassName?: string;
  /** Stacked modal (max 2 levels): inner level uses `1` */
  stackIndex?: 0 | 1;
}

function mapCommonSize(size: CommonModalSize): {
  modalSize: ModalSize;
  containerClassName?: string;
  scrollable: boolean;
} {
  switch (size) {
    case 'sm':
      return { modalSize: 'sm', scrollable: false };
    case 'md':
      return { modalSize: 'md', scrollable: false };
    case 'lg':
      return { modalSize: 'lg', scrollable: false };
    case 'xl':
      return { modalSize: 'lg', containerClassName: 'max-w-xl', scrollable: false };
    case '2xl':
      return { modalSize: 'lg', containerClassName: 'max-w-2xl', scrollable: false };
    case '3xl':
      return { modalSize: 'lg', containerClassName: 'max-w-3xl', scrollable: true };
    case 'full':
      return { modalSize: 'fullscreen', scrollable: true };
    default:
      return { modalSize: 'md', scrollable: false };
  }
}

/**
 * Compatibility layer: maps to {@link Modal} (system style & behavior).
 * New forms / dialogs should use `Modal` directly with `variant` / `primaryAction`.
 */
const CommonModal: React.FC<CommonModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  description,
  header,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  closeDisabled = false,
  hideCloseButton = false,
  className,
  bodyClassName,
  footerClassName,
  align = 'center',
  overlayClassName,
  zClassName = 'z-50',
  stackIndex = 0,
}) => {
  const { modalSize, containerClassName, scrollable } = mapCommonSize(size);

  return (
    <Modal
      stackIndex={stackIndex}
      isOpen={isOpen}
      onClose={onClose}
      title={header ? undefined : title}
      subtitle={header ? undefined : description}
      customHeader={header}
      variant={scrollable ? 'form' : 'default'}
      size={modalSize}
      containerClassName={containerClassName}
      scrollable={scrollable}
      footer={footer}
      preventClose={closeDisabled}
      showCloseButton={!hideCloseButton}
      closeOnOverlayClick={closeOnOverlayClick}
      verticalAlign={align === 'start' ? 'start' : 'center'}
      rootClassName={cn('!mt-0 overflow-y-auto', overlayClassName, zClassName)}
      className={className}
      bodyClassName={bodyClassName}
      footerClassName={footerClassName}
    >
      {children}
    </Modal>
  );
};

export default CommonModal;
